import React, { useEffect } from 'react';
import ProcessingModal from '@/components/dashboard-modal/ProcessingModal';
import { useResearchGenerationProgress } from '@/hooks/research-generator/use-research-generation-progress';
import { UseMutationResult } from '@tanstack/react-query';

interface ResearchGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  researchParams: any | null;
  researchMutation: UseMutationResult<any, Error, any, unknown>;
  onSuccess: (research: any) => void;
}

const ResearchGenerationModal: React.FC<ResearchGenerationModalProps> = ({
  isOpen,
  onClose,
  researchParams,
  researchMutation,
  onSuccess,
}) => {
  const { state, startGeneration, reset } = useResearchGenerationProgress(researchMutation);

  useEffect(() => {
    if (isOpen && researchParams && state.status === 'idle') {
      startGeneration(researchParams).then(
        (result) => {
          // Success is handled by the state update
          if (result) {
            setTimeout(() => {
              onSuccess(result);
            }, 500); // Small delay to show success state
          }
        },
        (error) => {
          // Error is handled by the state update
          console.error('Research generation failed:', error);
        }
      );
    }
  }, [isOpen, researchParams, startGeneration, state.status, onSuccess]);

  const handleClose = () => {
    reset();
    onClose();
  };
  
  const handleRetry = () => {
    if (researchParams) {
      startGeneration(researchParams);
    }
  };
  
  const handleSuccess = () => {
    handleClose();
  };

  // Calculate contextual success message
  const getSuccessMessage = () => {
    if (!researchParams) return 'Research generated successfully!';
    
    const { researchPrompt, researchType, timeframe, country } = researchParams;
    let message = `Your research has been generated`;
    
    if (researchType && researchType !== 'General Research') {
      message += ` for ${researchType}`;
    }
    
    if (timeframe && timeframe !== 'none') {
      message += ` (${timeframe})`;
    }
    
    if (country && country !== 'none') {
      message += ` in ${country}`;
    }
    
    return message + '!';
  };

  return (
    <ProcessingModal
      isOpen={isOpen}
      onClose={handleClose}
      state={state.status === 'processing' ? 'processing' : state.status === 'success' ? 'success' : 'error'}
      tasks={state.tasks}
      progress={state.progress}
      currentTaskDescription={state.currentTaskDescription}
      errorMessage={state.error || ''}
      onRetry={handleRetry}
      successMessage={getSuccessMessage()}
      successActionText="Continue"
      onSuccessAction={handleSuccess}
      title="🔬 Generating Research"
      description="Our AI is searching academic sources and creating comprehensive research based on your query."
    />
  );
};

export default ResearchGenerationModal;